import { useEffect, useMemo, useState } from "react"
import { useVectorModal } from "../../../hooks/useVectorMemoryModal"

import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    List,
    ListItem,
    Tab,
    Tabs,
    Tooltip,
    type DialogProps
} from "@mui/material"
import { FileText, X } from "@phosphor-icons/react"
import { VectorMetadataBulkChangeMetadataValue } from "./VectorMetadataBulkChangeMetadataValue"
import { VectorMetadataBulkChangeMetadata } from "./VectorMetadataBulkChangeMetadata"

import { BULK_MAX_DISPLAY_ITEMS } from "."

import type {
	MetadatasData,
	MetadataBulkTab,
	VectorFileData
} from "./vectorModal"
import type { NamespaceFile } from "../../../types/namespace"

interface VectorMetadataBulkEditModalProps extends DialogProps {
	onOpenChange: (open?: boolean) => void
}

function VectorMetadataBulkEditModal({
	open,
	onOpenChange,
	...props
}: VectorMetadataBulkEditModalProps) {
	const { tab: vectorTab } = useVectorModal()

	const {
		stagingFiles,
		selectedStagingFiles,

		storedFiles,
		selectedStoredFiles,
	} = useVectorModal()

	const [metadatasData, setMetadatasData] = useState<Array<MetadatasData>>([])
	/*
	const [metadatasCreated, setMetadatasCreated] = useState<Array<VectorMetadata>>([])
	const [metadatasDeleted, setMetadatasDeleted] = useState<Array<{ name: string }>>([])
	const [metadatasNameChanged, setMetadatasNameChanged] =
		useState<Array<{ oldName: string, newName: string }>>([])
	const [metadatasValueChanged, setMetadatasValueChanged] =
		useState<Array<{ name: string, newValue: string }>>([])
	*/

	const [tab, setTab] = useState<MetadataBulkTab>('change-label')

	const allFiles = useMemo(() => {
		if (vectorTab === 'edit-stored-files') {
			return storedFiles.filter(({ id }) => selectedStoredFiles.includes(id))
		}

		return stagingFiles.filter(({ id }) => selectedStagingFiles.includes(id))
	}, [vectorTab, storedFiles, selectedStoredFiles, stagingFiles, selectedStagingFiles])

	const visibleFiles = allFiles.length > BULK_MAX_DISPLAY_ITEMS
		? allFiles.slice(0, BULK_MAX_DISPLAY_ITEMS)
		: allFiles
	const hiddenFiles = visibleFiles.length < allFiles.length
		? allFiles.slice(BULK_MAX_DISPLAY_ITEMS) : null
	const filesIds = allFiles.map((file) => file.id)

	/*
	const handleTempMetadataCreate = ({
		name, value
	}: { name: string, value: string }) => {
		setMetadatasCreated((prev) => [...prev, { name, value }])
		setMetadatasData((prev) => [...prev, {
			name,
			values: [value],
			presentOn: filesIds
		}])
	}
	*/

	/*
	const handleTempMetadataDelete = ({ name }: { name: string }) => {
		setMetadatasDeleted((prev) => [...prev, { name }])

		const newMetadatasDataList = metadatasData.filter((metadata) => metadata.name === name)
		setMetadatasData(newMetadatasDataList)

		const newMetadatasCreatedList = metadatasCreated.filter((metadata) => metadata.name === name)
		setMetadatasCreated(newMetadatasCreatedList)
	}
	*/

	const handleResetTempState = () => {
		const data = transformMetadatasData(allFiles)

		setMetadatasData(data)

		/*
		setMetadatasCreated([])
		setMetadatasDeleted([])
		setMetadatasNameChanged([])
		setMetadatasValueChanged([])
		*/
	}

	const handleCloseDialog = () => {
		onOpenChange(false)
		handleResetTempState()
	}

	useEffect(() => {
		if (allFiles.length !== 0) {
			const data = transformMetadatasData(allFiles)

			setMetadatasData(data)
		}
	}, [allFiles])

	return (
		<Dialog
			open={open}
			fullWidth
			sx={{
				'.MuiPaper-root': {
					minWidth: '1000px',
					maxWidth: '1000px',
					borderRadius: '1rem',

					'&>*+*': {
						marginBlockStart: '1.5rem',
					}
				},
			}}
			{...props}
		>
			<header style={{ paddingBlockStart: '2rem', paddingInline: '2rem' }}>
				<DialogTitle sx={{ paddingInline: 0 }} fontSize="1.25rem">
					Manage Tags for selected files
				</DialogTitle>

				<List 
					sx={{
						paddingInline: 0,
						display: 'flex',
						flexWrap: 'wrap',
						gap: '1rem'
					}}
				>
					{visibleFiles.map(({ id, fileName }) => (
						<ListItem
							key={id}
							sx={{
								padding: '0.5rem',

								width: 'auto',
								height: '100%',

								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',

								backgroundColor: '#fafafa',

								borderStyle: 'solid',
								borderWidth: '1px',
								borderColor: '#d4d4d4',
								borderRadius: '0.5rem',
							}}
						>
							<FileText size={24} />
							{fileName}
						</ListItem>
					))}

					{hiddenFiles ? (
						<Tooltip
							title={
								<>
									{hiddenFiles.map(({ id, fileName }) => (
										<p key={id}>{fileName}</p>
									))}
								</>
							}
						>
							<p
								style={{
									display: 'flex',
									alignItems: 'center',
									margin: 0
								}}
							>
								+ {hiddenFiles.length} more
							</p>
						</Tooltip>
					) : null}
				</List>
			</header>

			<div style={{ paddingInline: '2rem' }}>
				<Divider />
			</div>

			<Tabs
				aria-label="Change metadata edit method"
				value={tab}
				onChange={(_, value) => {
					setTab(value as MetadataBulkTab)
				}}
				sx={{
					paddingInline: '2rem',

					'.MuiTabs-flexContainer': {
						gap: '1rem'
					}
				}}
			>
				<Tab
					label="Change Label"
					value="change-label"
					id="tab-change-label"
					aria-controls="tabpanel-bulk-metadata"
				/>

				<Tab
					label="Change Label Value"
					value="change-label-value"
					id="tab-change-label-value"
					aria-controls="tabpanel-bulk-metadata"
				/>
			</Tabs>
				
			<VectorMetadataBulkChangeMetadata
				filesIds={filesIds}
				metadatasData={metadatasData}
				selectedTab={tab}
				// onTempMetadataCreate={handleTempMetadataCreate}
			/>
			<VectorMetadataBulkChangeMetadataValue 
				filesIds={filesIds}
				metadatasData={metadatasData}
				selectedTab={tab}
			/>

			<DialogActions
				sx={{
					borderBlockStartWidth: '1px',
					borderBlockStartStyle: 'solid',
					borderBlockStartColor: '#EAECF0',

					paddingInline: '2rem',
					paddingBlock: '1rem'
				}}
			>
				<Button
					variant="contained"
					onClick={() => handleCloseDialog()}
				>
					Save Changes
				</Button>
			</DialogActions>

			<Button
				sx={{
					position: 'absolute',
					right: '1.25rem',
					top: '1.25rem',
					padding: '0.25rem',
					minWidth: 'auto',
					zIndex: '10',
					margin: '0 !important',
				}}
				onClick={() => handleCloseDialog()}
			>
				<span style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					padding: 0,
					margin: '-1px',
					overflow: 'hidden',
					clip: 'rect(0, 0, 0, 0)',
					whiteSpace: 'nowrap',
					borderWidth: 0
				}}>
					Close
				</span>

				<X width={24} height={24} />
			</Button>
		</Dialog>
	)
}

function transformMetadatasData(
	files: Array<VectorFileData | NamespaceFile>
): Array<MetadatasData> {
	const metadataMap = new Map()
	
	files.forEach(file => {
		file.metadatas.forEach(metadata => {
			const metadataName = metadata.name
			const metadataValue = metadata.value
			
			if (!metadataMap.has(metadataName)) {
				metadataMap.set(metadataName, {
					name: metadataName,
					values: [],
					presentOn: []
				});
			}
			
			const metadataData = metadataMap.get(metadataName)
			
			if (!metadataData.presentOn.includes(file.id)) {
				metadataData.presentOn.push(file.id)
			}
			
			if (!metadataData.values.includes(metadataValue)) {
				metadataData.values.push(metadataValue)
			}
		})
	})
	
	return Array.from(metadataMap.values());
}

export { VectorMetadataBulkEditModal }